import { useEffect } from 'react';
import { absoluteUrl, DEFAULT_OG_IMAGE, setStructuredData, SITE_NAME } from '../lib/seo';

interface PageMeta {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  type?: 'website' | 'article';
  imageAlt?: string;
  keywords?: string;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  schema?: unknown;
}

const upsertMeta = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
};

export const usePageMeta = ({
  title,
  description,
  canonicalPath,
  image,
  type = 'website',
  imageAlt = `${SITE_NAME} study abroad consultancy`,
  keywords,
  noindex = false,
  publishedTime,
  modifiedTime,
  schema,
}: PageMeta) => {
  useEffect(() => {
    document.title = title;
    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[name="robots"]', 'name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
    upsertMeta('meta[name="author"]', 'name', 'author', SITE_NAME);
    upsertMeta('meta[name="content-language"]', 'name', 'content-language', 'en-IN');
    upsertMeta('meta[name="geo.region"]', 'name', 'geo.region', 'IN-KL');
    upsertMeta('meta[name="geo.placename"]', 'name', 'geo.placename', 'Kochi, Kerala, India');
    if (keywords) upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', 'en_IN');
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    const canonicalUrl = absoluteUrl(canonicalPath || window.location.pathname);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);

    const imageUrl = absoluteUrl(image || DEFAULT_OG_IMAGE);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', imageAlt);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    upsertMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', imageAlt);
    if (publishedTime) upsertMeta('meta[property="article:published_time"]', 'property', 'article:published_time', publishedTime);
    if (modifiedTime) upsertMeta('meta[property="article:modified_time"]', 'property', 'article:modified_time', modifiedTime);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    let language = document.head.querySelector<HTMLLinkElement>('link[rel="alternate"][hreflang="en-IN"]');
    if (!language) {
      language = document.createElement('link');
      language.rel = 'alternate';
      language.hreflang = 'en-IN';
      document.head.appendChild(language);
    }
    language.href = canonicalUrl;

    let defaultLanguage = document.head.querySelector<HTMLLinkElement>('link[rel="alternate"][hreflang="x-default"]');
    if (!defaultLanguage) {
      defaultLanguage = document.createElement('link');
      defaultLanguage.rel = 'alternate';
      defaultLanguage.hreflang = 'x-default';
      document.head.appendChild(defaultLanguage);
    }
    defaultLanguage.href = canonicalUrl;
    setStructuredData('gec-page-schema', schema);
  }, [canonicalPath, description, image, imageAlt, modifiedTime, noindex, publishedTime, schema, title, type, keywords]);
};
